'use client';

import { useFreelancersStats } from '@/graphql/auth';
import { motion } from 'framer-motion';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { FaSearch, FaUsers, FaChartLine } from 'react-icons/fa';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

export function HeroSection() {

  const locale = useLocale();
  const isRtl = locale === "ar";
  const router = useRouter();
  const t = useTranslations("hero");

  const { totalActiveFreelancers, topCategories, loading } = useFreelancersStats();
  const hasTopCategories = !loading && topCategories && topCategories?.length > 0;

  return (
    <section
      className="
        relative 
        min-h-screen 
        md:px-8 
        flex 
        items-center 
        justify-center 
        overflow-hidden
        bg-[url('/imgs/hero.jpeg')] 
        bg-cover 
        bg-center 
        bg-no-repeat
      "
    >
      <div className="absolute inset-0 bg-black/60 z-0" />

      <div className="relative z-10 container mx-auto px-4 py-16">
        <motion.div
          className="flex flex-col lg:flex-row items-center gap-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
        >
          <motion.div
            className={`w-full lg:w-1/2 text-center ${isRtl ? "lg:text-right" : "lg:text-left"}`}
            variants={itemVariants}
          >
            <motion.h1
              className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6"
              variants={itemVariants}
            >
              {t("headline")}
            </motion.h1>

            <motion.p
              className="text-lg md:text-xl text-gray-200 mb-8 max-w-lg mx-auto lg:mx-0"
              variants={itemVariants}
            >
              {t("description")}
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12"
              variants={itemVariants}
            >
              <button
                onClick={() => router.push("/freelancers")}
                className="px-8 py-3 bg-white text-black font-medium rounded-lg hover:bg-gray-200 transition"
              >
                {t("searchButton")}
              </button>

              <button
                onClick={() => router.push("/sign-up")}
                className="px-8 py-3 border border-white text-white font-medium rounded-lg hover:bg-white hover:text-black transition"
              >
                {t("beConsultant")}
              </button>
            </motion.div>

            <div className="grid grid-cols-3 gap-8 max-w-md mx-auto lg:mx-0">
              {[
                {
                  value: loading
                    ? "..."
                    : `${totalActiveFreelancers + 50}+`,
                  label: "consultants"
                },
                { value: "24/7", label: "availability" },
                { value: "100%", label: "satisfaction" }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  variants={itemVariants}
                >
                  <div className="text-2xl font-bold text-white">
                    {stat.value}
                  </div>
                  <div className="text-gray-300 text-sm">
                    {t(stat.label)}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {hasTopCategories && (
            <motion.div
              className="w-full lg:w-1/2 flex justify-center"
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
              }}
            >
              <div className="relative w-full max-w-md">
                <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-2xl p-6 border border-white/20">
                  <div className="flex items-center mb-6">
                    <div
                      onClick={() => router.push("/freelancers")}
                      className="w-12 h-12 rounded-lg flex items-center justify-center bg-white/20 cursor-pointer"
                    >
                      <motion.div
                        whileHover={{
                          rotate: 90,
                          transition: { type: "spring", stiffness: 300 }
                        }}
                        whileTap={{ scale: 0.9 }}
                        className="flex items-center justify-center"
                      >
                        <FaSearch className="text-white text-xl" />
                      </motion.div>
                    </div>
                    <div className="mx-4">
                      <h3 className="font-semibold text-white">
                        {t("searchTitle")}
                      </h3>
                      <p className="text-sm text-gray-300">
                        {t("searchDesc")}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {loading ? (
                      <p className="text-gray-300 text-sm">...</p>
                    ) : (
                      topCategories.map((category: any, index: number) => (
                        <div key={category.categoryId} className="flex items-center">
                          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mx-3 text-white">
                            {index === 0 ? <FaUsers /> : <FaChartLine />}
                          </div>
                          <div className="text-sm">
                            <p className="font-medium text-white">{t(category.name)}</p>
                            <p className="text-gray-300">
                              {category.totalFreelancers + 50} {t("consultants")}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <button
                    onClick={() => router.push("/projects")}
                    className="w-full mt-6 py-3 bg-white text-black font-medium rounded-lg hover:bg-gray-200 transition"
                  >
                    {t("projectsButton")}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
}